/**
 * Analytical multitasking detection via interval overlap.
 * Replaces the day-by-day simulation's multitasking logic.
 */

import type { Task } from '../../types';
import type { TaskBaseDuration } from './taskDuration';

const DEFAULT_PENALTY = 0.3;

// ============================================
// Types
// ============================================

export interface MultitaskingResult {
  taskId: string;
  adjustedEffectiveDays: number;  // After multitasking correction
  multitaskingDayCount: number;   // Number of affected days
  penaltyFactor: number;          // 1.0 = no penalty
}

// ============================================
// Interval overlap detection
// ============================================

interface TaskInterval {
  taskId: string;
  column: string;
  startDay: number;
  endDay: number; // inclusive
  effectiveDays: number;
}

export function applyMultitaskingPenalties(
  baseDurations: Map<string, TaskBaseDuration>,
  tasks: Task[],
  penalty: number = DEFAULT_PENALTY,
): Map<string, MultitaskingResult> {
  const results = new Map<string, MultitaskingResult>();

  // Initialize results with no-penalty defaults
  for (const [taskId, dur] of baseDurations) {
    results.set(taskId, {
      taskId,
      adjustedEffectiveDays: dur.effectiveDays,
      multitaskingDayCount: 0,
      penaltyFactor: 1.0,
    });
  }

  // Group tasks by primary assignee (first in assigneeIds)
  const tasksByPrimary = new Map<string, TaskInterval[]>();

  for (const task of tasks) {
    if (task.assigneeIds.length === 0) continue;
    const primaryId = task.assigneeIds[0];
    const dur = baseDurations.get(task.id);
    if (!dur) continue;

    const interval: TaskInterval = {
      taskId: task.id,
      column: task.column,
      startDay: dur.startDay,
      endDay: dur.endDay,
      effectiveDays: dur.effectiveDays,
    };

    let list = tasksByPrimary.get(primaryId);
    if (!list) {
      list = [];
      tasksByPrimary.set(primaryId, list);
    }
    list.push(interval);
  }

  // For each employee, detect cross-column overlaps
  for (const [, intervals] of tasksByPrimary) {
    if (intervals.length < 2) continue;

    // Sort by start day
    intervals.sort((a, b) => a.startDay - b.startDay);

    // For each pair of tasks in different columns, check overlap
    for (let i = 0; i < intervals.length; i++) {
      const taskA = intervals[i];
      let overlapDays = 0;
      let maxThemeCount = 1;

      for (let j = 0; j < intervals.length; j++) {
        if (i === j) continue;
        const taskB = intervals[j];

        // Different column = different theme = multitasking
        if (taskA.column === taskB.column) continue;

        // Check overlap: [startA, endA] ∩ [startB, endB]
        const overlapStart = Math.max(taskA.startDay, taskB.startDay);
        const overlapEnd = Math.min(taskA.endDay, taskB.endDay);

        if (overlapStart <= overlapEnd) {
          const thisOverlap = overlapEnd - overlapStart + 1;
          overlapDays += thisOverlap;

          // Count distinct columns overlapping at any point
          // (simplified: count max concurrent themes)
          maxThemeCount = Math.max(maxThemeCount, 2);
        }
      }

      if (overlapDays <= 0) continue;

      // Deduplicate overlap days (can't exceed task duration)
      overlapDays = Math.min(overlapDays, taskA.effectiveDays);

      // Calculate speed factor
      // Non-overlapping part runs at full speed, overlapping part is penalized
      const overlapFraction = overlapDays / taskA.effectiveDays;
      const themeCount = maxThemeCount;
      const speedFactor = (1 - overlapFraction) + overlapFraction * ((1 - penalty) / themeCount);

      const adjustedEffectiveDays = Math.ceil(taskA.effectiveDays / speedFactor);

      results.set(taskA.taskId, {
        taskId: taskA.taskId,
        adjustedEffectiveDays,
        multitaskingDayCount: overlapDays,
        penaltyFactor: speedFactor,
      });
    }
  }

  return results;
}
